import React, { useContext, useEffect, useMemo } from 'react';

import {

    BatteryHigh,

    Check,

    Eye,

    House,

    Lightning,

    Minus,

    Plus,

    Plugs,

    SunHorizon,

    CaretRight,

} from '@phosphor-icons/react';

import { useDashboardV3Data, type V3ProductCardModel } from './sections/dashboardV3/useDashboardV3Data';

import { roofTextureList } from '../../../../Constants';

import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../store/store';

import { ProductFinanceHubRow } from './ProductFinanceHubRow';
import { ProductsFinanceHubContext } from './ProductsFinanceHubContext';
import { useProductPartnerSync } from './useProductPartnerSync';

import './oneButton-css/ProductsFinanceHubSection.css';
import { SOLAR_NON_FINALIZED_WARNING, ONEDRAW_DESIGN_NOT_LOADED_MESSAGE } from '../../../../helpers/systemDesignSolarMetrics';
import { cardKindToPricingToggleProduct } from '../../../../helpers/financeSelectors';
import { evaluateGenerateQuoteSyncGate } from './generateQuoteSyncGate';
import { isPricingProductCached } from '../../../../helpers/pricingSessionCache';
import { useSiteViewer3dBlocked } from '../../../../helpers/siteViewer3dAccess';
import { isHoverRoofDesignEnabled } from '../../../../services/oneButtonServices/hoverRoofDesignGate';
import HoverLogoMark from './HoverLogoMark';
import DesignLossesSourceBadge from './DesignLossesSourceBadge';

export type SystemDesignProductCardProps = {

    model: V3ProductCardModel;

    onDesign?: () => void;

    /** Button label for Design action (default "Design"). Pass a node for Hover logo + "Hover Roof Design". */
    designLabel?: React.ReactNode;

    onUtility?: () => void;

    onAdders?: () => void;

    onOpenProducts?: () => void;

    onWastageDecrease?: () => void;

    onWastageIncrease?: () => void;

    onShingleClick?: () => void;

    showFinanceProductRow?: boolean;

    financeProductName?: string;

    headEndActions?: React.ReactNode;

    /** Create / open Hover job — roof card only when roof product is selected. */
    onHoverJobClick?: () => void;
    hoverJobAvailable?: boolean;
    hoverJobBusy?: boolean;
};



const ICONS = {

    solar: SunHorizon,

    roof: House,

    battery: BatteryHigh,

    gutter: House,

} as const;

/**
 * Partner Sync control for the card header. Mirrors the Products hub row's Partner Sync via the
 * shared hook, so it only appears for an active, financed (non-cash) product and routes to the
 * same sync flow. Rendered only inside the finance hub provider (embedded finance card).
 */
const SystemDesignCardHeadToolbar: React.FC<{
    partnerSyncProductName?: string;
    headEndActions?: React.ReactNode;
    highlightSyncRequired?: boolean;
    /** When set, overrides `canSync` for button visibility (Roof keeps its own sync entry point). */
    showPartnerSync?: boolean;
    /** Roof: sync uses opportunity partner even when the hub product line is inactive. */
    allowInactivePartnerSync?: boolean;
}> = ({
    partnerSyncProductName,
    headEndActions,
    highlightSyncRequired = false,
    showPartnerSync,
    allowInactivePartnerSync = false,
}) => {
    const { canSync, syncDisabled, designBlocked, syncBlockedReason, handlePartnerSync } =
        useProductPartnerSync(partnerSyncProductName ?? '', {
            allowInactiveProduct: allowInactivePartnerSync,
        });

    const partnerSyncVisible = showPartnerSync ?? canSync;

    const partnerSyncButton =
        partnerSyncProductName && partnerSyncVisible ? (
            <button
                type="button"
                className={`obd-merged-system__chip-btn obd-sd-card__partner-sync${
                    highlightSyncRequired ? ' is-sync-required' : ''
                }${syncDisabled || designBlocked ? ' is-disabled' : ''}${
                    designBlocked ? ' is-design-blocked' : ''
                }`}
                disabled={syncDisabled}
                aria-disabled={syncDisabled || designBlocked || undefined}
                onClick={handlePartnerSync}
                title={
                    syncBlockedReason ??
                    (syncDisabled ? 'Partner Sync unavailable' : 'Sync design with finance partner')
                }
                aria-label="Partner Sync"
            >
                <Plugs size={16} weight="duotone" aria-hidden />
                <span>Partner Sync</span>
            </button>
        ) : null;

    if (!partnerSyncButton && !headEndActions) return null;

    return (
        <div
            className="obd-financing__toolbar obd-metrics__action-group obd-sd-card__head-actions"
            role="group"
            aria-label="Solar design shortcuts"
        >
            {partnerSyncButton}
            {headEndActions}
        </div>
    );
};



export const SystemDesignProductCard: React.FC<SystemDesignProductCardProps> = ({

    model,

    onDesign,

    designLabel = 'Design',

    onUtility,

    onAdders,

    onOpenProducts,

    onWastageDecrease,

    onWastageIncrease,

    onShingleClick,

    showFinanceProductRow = false,

    financeProductName,

    headEndActions,

    onHoverJobClick,

    hoverJobAvailable = false,

    hoverJobBusy = false,

}) => {

    const Icon = ICONS[model.kind];

    const showCheck = model.statusTone === 'signed';

    const financeHubCtx = useContext(ProductsFinanceHubContext);
    const embedFinanceRow = showFinanceProductRow && !!financeProductName && !!financeHubCtx;
    // Solar keeps metrics (no finance embed), but still shares the hub partner — expose Partner Sync
    // from the header using the same product line the Products popup would use.
    const partnerSyncProductName =
        financeProductName ??
        (model.kind === 'solar'
            ? 'Solar'
            : model.kind === 'roof'
              ? 'Roof'
              : model.kind === 'battery'
                ? 'Battery'
                : undefined);

    const hubRowActive =
        embedFinanceRow && financeProductName
            ? !!financeHubCtx?.getRow(financeProductName)?.activeFlag
            : false;

    const financeDisplay = (() => {
        const fallback = { partner: model.financePartner, method: model.financeMethod };
        if (!financeHubCtx || !partnerSyncProductName) return fallback;
        if (financeHubCtx.hasProductUnsavedChanges(partnerSyncProductName)) return fallback;
        const row = financeHubCtx.getRow(partnerSyncProductName);
        const hubPartner = String(row?.finance_Partner_Display_Name ?? '').trim();
        const hubMethod = String(row?.finance_Method_Name ?? '').trim();
        const placeholder = /^(select\s+(finance\s+)?partner|select\s+finance|select\s+method)$/i;
        if (!hubPartner || placeholder.test(hubPartner)) return fallback;
        return {
            partner: hubPartner,
            method: hubMethod && !placeholder.test(hubMethod) ? hubMethod : fallback.method,
        };
    })();



    const opportunity = useOneButtonStore((s) => s.opportunityDetails);
    const systemDesignLosses = useOneButtonStore((s) => s.systemDesignLosses);
    const financePartnerSyncStatus = useOneButtonStore((s) => s.financePartnerSyncStatus);
    const financePartnerRawSyncStatuses = useOneButtonStore((s) => s.financePartnerRawSyncStatuses);
    const hoverJobId = useOneButtonStore((s) => s.hoverJobId);
    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const togglePricingProductEnabled = useOneButtonStore((s) => s.togglePricingProductEnabled);
    const pricingDesignFingerprint = useOneButtonStore((s) => s.pricingDesignFingerprint);
    const pricingToggleProduct = cardKindToPricingToggleProduct(model.kind);
    const pricingIncluded = useOneButtonStore((s) =>
        pricingToggleProduct ? s.pricingProductsEnabled[pricingToggleProduct] : false,
    );
    const pricingCached =
        pricingToggleProduct != null &&
        isPricingProductCached(pricingDesignFingerprint, pricingToggleProduct);

    const quoteSyncGate = useMemo(
        () =>
            evaluateGenerateQuoteSyncGate({
                opportunityDetails: opportunity,
                financePartnerSyncStatus,
                financePartnerRawSyncStatuses,
                scope: partnerSyncProductName
                    ? {
                          quoteProductTypes: [
                              partnerSyncProductName as 'Solar' | 'Roof' | 'Battery',
                          ],
                      }
                    : undefined,
            }),
        [
            opportunity,
            financePartnerSyncStatus,
            financePartnerRawSyncStatuses,
            partnerSyncProductName,
        ],
    );

    const systemDesign = useOneButtonStore((s) => s.systemDesign)

    const siteMultipliersPendingNotice = useViewerStore((s) => s.siteMultipliersPendingNotice);

    const { solarMetricsOutOfSync } = useDashboardV3Data();
    const showNonFinalizedMetricsWarning = solarMetricsOutOfSync && model.kind === 'solar';
    const { blocked: siteViewerUnavailable } = useSiteViewer3dBlocked();

    const hasFinalizedSolarDesign = Boolean(
        systemDesign?.DesignArray != null &&
            systemDesign.DesignArray.length > 0 &&
            (systemDesign.DesignArray as { ArrayId?: unknown }[])[0]?.ArrayId,
    );
    const hasSolarInterest = Boolean(
        (opportunity?.OpportunityInterest ?? opportunity?.opportunityInterest ?? systemDesign?.OpportunityInterest)
            ?.Solar,
    );
    const showOneDrawNotLoadedAlert = model.kind === 'solar' && siteViewerUnavailable;
    const showFinalizeDesignAlert =
        model.kind === 'solar' &&
        hasSolarInterest &&
        !siteViewerUnavailable &&
        systemDesign &&
        !hasFinalizedSolarDesign;
    const showSitePendingNotice = model.kind === 'solar' && !!siteMultipliersPendingNotice;


    const shingleMatched = (shingleProductCode: string) => {

        return roofTextureList.find((t) => t.name === shingleProductCode);

    };

    const shingleTexture =
        model.kind === 'roof' && model.shingleSwatch
            ? shingleMatched(model.shingleSwatch)
            : undefined;
    const hasShinglePreview = Boolean(shingleTexture?.img);

    const clickHintIcon = (
        <span className="obd-sd-card__click-hint" aria-hidden>
            <CaretRight size={12} weight="bold" />
        </span>
    );

    const haveProduct = (product: string) => {

        const matched = opportunity?.OpportunityProduct.find(

            (ele: any) => ele.Product_Type_Name.toLowerCase() === product.toLowerCase()

        );

        return !!matched;

    };



    const canOpenDesign = haveProduct(model.kind) || hubRowActive;
    const showHoverJobAction =
        model.kind === 'roof' && haveProduct('roof') && typeof onHoverJobClick === 'function';
    const hideRoofDesignAction = model.kind === 'roof' && hoverJobAvailable;
    const isHoverRoofDesign = useMemo(
        () =>
            model.kind === 'roof' &&
            isHoverRoofDesignEnabled({
                hoverJobId,
                opportunityDetails: opportunity,
                onebuttonCapabilities,
            }),
        [model.kind, hoverJobId, opportunity, onebuttonCapabilities],
    );

    const hasAssignedFinancePartner = (() => {
        const placeholder = /^(select\s+(finance\s+)?partner|select\s+finance|select\s+method)$/i;
        if (embedFinanceRow && financeProductName && financeHubCtx) {
            const row = financeHubCtx.getRow(financeProductName);
            const partner = String(row?.finance_Partner_Display_Name ?? '').trim();
            if (partner.length > 0 && !placeholder.test(partner)) return true;
            return financeHubCtx.hasSavedFinancePartnerForProduct(financeProductName);
        }
        const partner = financeDisplay.partner.trim();
        return partner.length > 0 && !placeholder.test(partner);
    })();

    const showRoofPartnerSync =
        model.kind === 'roof' &&
        hasAssignedFinancePartner &&
        !String(financeDisplay.partner).toLowerCase().includes('cash');

    const partnerSyncControl =
        partnerSyncProductName && financeHubCtx ? (
            <SystemDesignCardHeadToolbar
                partnerSyncProductName={partnerSyncProductName}
                headEndActions={headEndActions}
                highlightSyncRequired={quoteSyncGate.blocked}
                showPartnerSync={model.kind === 'roof' ? showRoofPartnerSync : undefined}
                allowInactivePartnerSync={model.kind === 'roof'}
            />
        ) : headEndActions ? (
            <div
                className="obd-financing__toolbar obd-metrics__action-group obd-sd-card__head-actions"
                role="group"
                aria-label="Solar design shortcuts"
            >
                {headEndActions}
            </div>
        ) : null;

    return (

        <article
            className={`obd-sd-card obd-sd-card--${model.kind}${showNonFinalizedMetricsWarning ? ' obd-sd-card--metrics-out-of-sync' : ''}`}
            data-status={model.statusTone}
        >

            <header className="obd-sd-card__head">

                <div className="obd-sd-card__head-main">

                    <span className="obd-sd-card__icon" aria-hidden>

                        <Icon size={20} weight="duotone" />

                    </span>

                    <div className="obd-sd-card__head-text">

                        <h3 className="obd-sd-card__title">{model.title}</h3>

                        <p className="obd-sd-card__subtitle">{model.subtitle}</p>
                        {model.fallbackHint ? (
                            <span className="obd-sd-card__fallback-flag">{model.fallbackHint}</span>
                        ) : null}

                    </div>

                </div>

                <div className="obd-sd-card__head-end">
                    {model.kind === 'solar' ? (
                        <DesignLossesSourceBadge lossesSource={systemDesignLosses?.LossesSource} />
                    ) : null}
                    {model.statusTone === 'signed' ? (
                        <span className={`obd-sd-card__badge obd-sd-card__badge--${model.statusTone}`}>
                            {showCheck ? <Check size={11} weight="bold" aria-hidden /> : null}
                            {model.statusLabel}
                        </span>
                    ) : null}
                    {partnerSyncControl}
                </div>

            </header>



            <div className="obd-sd-card__body">

                {embedFinanceRow ? (

                    <div className="obd-sd-card__finance-embed">
                        <ProductFinanceHubRow
                            productName={financeProductName}
                            hidePartnerSync
                            showSaveAction
                        />
                    </div>

                ) : (

                    <>
                        {showSitePendingNotice ? (
                            <p className="obd-sd-card__warning obd-sd-card__warning--site-pending" role="status">
                                {siteMultipliersPendingNotice}
                            </p>
                        ) : null}
                        {showNonFinalizedMetricsWarning ? (
                            <p className="obd-sd-card__warning" role="status">
                                {SOLAR_NON_FINALIZED_WARNING}
                            </p>
                        ) : null}
                        {showOneDrawNotLoadedAlert ? (
                            <div className="design-not-finalized-alert" role="status">
                                {ONEDRAW_DESIGN_NOT_LOADED_MESSAGE}
                            </div>
                        ) : showFinalizeDesignAlert ? (
                            <div className="design-not-finalized-alert" role="status">
                                Finalize the design in oneDRAW, then refresh and save the Design. Once completed, you can proceed
                            </div>
                        ) : null}
                        <div className="obd-sd-card__metrics-panel">
                        <dl className="obd-sd-card__metrics">

                            {model.metrics.map((m) => (

                                <div

                                    key={m.label}

                                    className={`obd-sd-card__metric${m.emphasis ? ' obd-sd-card__metric--emphasis' : ''}${m.label.toLowerCase() === 'multiplier' ? ' obd-sd-card__metric--multiplier' : ''}`}

                                >

                                    <dt className="obd-sd-card__metric-label">{m.label}</dt>

                                    <dd className="obd-sd-card__metric-value">
                                        {m.value}
                                        {m.liveHint ? (
                                            <span
                                                className="obd-sd-card__metric-live-hint"
                                                title="Live oneDRAW value — finalize the design to match"
                                            >
                                                ({m.liveHint})
                                            </span>
                                        ) : null}
                                    </dd>

                                </div>

                            ))}

                            {model.kind === 'roof' && model.showWastage ? (
                                <>
                                    <div className="obd-sd-card__metric">
                                        <dt className="obd-sd-card__metric-label">Wastage %</dt>
                                        <dd
                                            className="obd-sd-card__metric-value"
                                            title="Wastage adjustment (coming soon)"
                                        >
                                            {model.wastagePercent != null ? `${model.wastagePercent}%` : '—'}
                                        </dd>
                                    </div>
                                    <button
                                        type="button"
                                        className="obd-sd-card__metric obd-sd-card__metric--action obd-sd-card__metric--shingle-link"
                                        aria-label={`Shingle: ${model.shingleLabel ?? '—'}`}
                                        onClick={onShingleClick}
                                    >
                                        <dt className="obd-sd-card__metric-label">Shingle</dt>
                                        <dd className="obd-sd-card__metric-value">
                                            {model.shingleLabel ?? '—'}
                                        </dd>
                                        {clickHintIcon}
                                    </button>
                                    <button
                                        type="button"
                                        className="obd-sd-card__metric obd-sd-card__metric--action obd-sd-card__metric--shingle-preview"
                                        aria-label={
                                            hasShinglePreview
                                                ? `Shingle preview: ${model.shingleLabel ?? '—'}`
                                                : 'No shingle selected'
                                        }
                                        onClick={onShingleClick}
                                    >
                                        {hasShinglePreview ? (
                                            <span
                                                className="obd-sd-card__shingle-preview-fill"
                                                style={{
                                                    backgroundImage: `url(${shingleTexture!.img})`,
                                                }}
                                                aria-hidden
                                            />
                                        ) : (
                                            <span
                                                className="obd-sd-card__shingle-preview-fill obd-sd-card__shingle-preview-fill--empty"
                                                aria-hidden
                                            >
                                                <span className="obd-sd-card__shingle-preview-placeholder-text">
                                                    NO SHINGLE
                                                </span>
                                            </span>
                                        )}
                                        {clickHintIcon}
                                    </button>
                                </>
                            ) : null}

                        </dl>
                        </div>

                    </>

                )}

            </div>



            <footer className="obd-sd-card__foot">

                {!embedFinanceRow ? (
                    <div className="obd-sd-card__finance-bar">
                        <button
                            type="button"
                            className={`obd-sd-card__finance${hasAssignedFinancePartner ? '' : ' obd-sd-card__finance--needs-partner'}`}
                            onClick={onOpenProducts}
                            aria-label={`Finance: ${financeDisplay.partner}${financeDisplay.method ? `, ${financeDisplay.method}` : ''}`}
                        >
                            <span className="obd-sd-card__finance-partner">{financeDisplay.partner}</span>
                            {financeDisplay.method ? (
                                <span className="obd-sd-card__finance-method">{financeDisplay.method}</span>
                            ) : null}
                            {clickHintIcon}
                        </button>
                        {pricingToggleProduct ? (
                            <button
                                type="button"
                                className={`obd-sd-card__finance-toggle${pricingIncluded ? ' is-on' : ''}${
                                    pricingCached ? ' has-cache' : ''
                                }`}
                                aria-pressed={pricingIncluded}
                                aria-label={
                                    pricingIncluded
                                        ? `Exclude ${pricingToggleProduct} from pricing`
                                        : `Include ${pricingToggleProduct} in pricing`
                                }
                                title={
                                    pricingIncluded
                                        ? 'Included in pricing — click to exclude'
                                        : pricingCached
                                          ? 'Cached pricing available — click to include'
                                          : 'Excluded from pricing — click to fetch'
                                }
                                onClick={(event) => {
                                    event.stopPropagation();
                                    togglePricingProductEnabled(pricingToggleProduct);
                                }}
                            >
                                <span className="obd-sd-card__finance-toggle-switch" aria-hidden>
                                    <span className="obd-sd-card__finance-toggle-track">
                                        <span className="obd-sd-card__finance-toggle-thumb">
                                            {pricingIncluded ? (
                                                <Check size={10} weight="bold" aria-hidden />
                                            ) : null}
                                        </span>
                                    </span>
                                </span>
                                <span className="obd-sd-card__finance-toggle-copy">
                                    <span className="obd-sd-card__finance-toggle-label">Price</span>
                                    <span className="obd-sd-card__finance-toggle-hint">
                                        {pricingIncluded ? 'Included' : pricingCached ? 'Ready' : 'Off'}
                                    </span>
                                </span>
                            </button>
                        ) : null}
                    </div>
                ) : null}

                {hasAssignedFinancePartner ? (
                    <div className="obd-sd-card__actions" role="group" aria-label="Product actions">
                        {showHoverJobAction ? (
                            <button
                                type="button"
                                className={`obd-sd-card__action obd-sd-card__action--hover-job${
                                    hoverJobAvailable ? ' obd-sd-card__action--hover-job-active' : ''
                                }${hoverJobBusy ? ' obd-sd-card__action--hover-job-busy' : ''}`}
                                onClick={onHoverJobClick}
                                disabled={hoverJobBusy}
                                aria-label={hoverJobAvailable ? 'Open Hover Job' : 'Create Hover Job'}
                                title={hoverJobAvailable ? 'Open Hover Job' : 'Create Hover Job'}
                            >
                                <HoverLogoMark
                                    size="sm"
                                    className={hoverJobAvailable ? 'obd-hover-logo-mark--active' : ''}
                                />
                            </button>
                        ) : null}
                        {!hideRoofDesignAction ? (
                            <button
                                type="button"
                                className={`obd-sd-card__action${
                                    isHoverRoofDesign ? ' obd-sd-card__action--hover-roof-design' : ''
                                }`}
                                onClick={onDesign}
                                disabled={!canOpenDesign}
                            >
                                {typeof designLabel === 'string' ? (
                                    <>
                                        <Eye size={15} weight="regular" aria-hidden />
                                        {designLabel}
                                    </>
                                ) : (
                                    designLabel
                                )}
                            </button>
                        ) : null}

                        {model.kind === 'solar' || (model.kind === 'battery' && !haveProduct('solar')) ? (
                            <button type="button" className="obd-sd-card__action" onClick={onUtility}>
                                <Lightning size={15} weight="duotone" aria-hidden />
                                Utility
                            </button>
                        ) : null}

                        <button
                            type="button"
                            className="obd-sd-card__action"
                            onClick={onAdders}
                            title="Adders"
                            disabled={!canOpenDesign}
                        >
                            <Plus size={14} weight="bold" aria-hidden />
                            Adders
                        </button>
                    </div>
                ) : null}

            </footer>

        </article>

    );

};

